import {FC, useCallback} from 'react';
import {FetchStatus} from '../../../../common/FetchStatus.enum';
import {t} from '../../../../common/getTranslations';
import {requestContext} from '../../../../common/requestContext';
import {twMerge} from '../../../../common/twMerge';
import {useThunkDispatch} from '../../../../common/useThunkDispatch.hook';
import useTypedSelector from '../../../../common/useTypedSelector';
import {GroupType, SectionType} from '../../../../graphql/generated/types';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {
  fetchDynamicLayoutByGroupId,
  fetchViewDataByGroupId,
} from '../../../../pagewebLayout/PagewebLayoutSlice';
import {useSelectGroup} from '../../../../pagewebLayout/useSelectGroup';
import {showToast} from '../../../../toast/toastHandler';
import {CommonGroupProps} from '../../type';
import {Hidden} from '../Hidden';
import Item from '../Items';
import {Banner} from './Banner';
import {DisplayAd} from './DisplayAd';
import EventBannerGrid from './EventBannerGrid';
import {HorizontalScroll} from './HorizontalScroll';
import {MainRankingNormalList} from './MainRankingNormalList';
import {NormalList} from './NormalList';
import {PassChildren} from './PassChildren';
import {ResponsiveBanner} from './ResponsiveBanner';
import {SkeletonLazyLoad} from './SkeletonLazyLoad';
import StandaloneBannerAndSubCard from './StandaloneBannerAndSubCard';
import StrategyCategory from './StrategyCategory';
import SubCard from './SubCard';
import {ThumbnailGrid} from './ThumbnailGrid';
import TodaySeries from './TodaySeries';

function Group({id}: {id: string}) {
  const {group, fetchStatus} = useSelectGroup(id);
  const {groupsInGroup, itemsInGroup, parentSection} = useTypedSelector(
    state => ({
      groupsInGroup: state.pagewebLayout.relations.nestedGroups[id].childrenIds,
      itemsInGroup: state.pagewebLayout.relations.groups[id].childrenIds,
      parentSection: state.pagewebLayout.relations.groups[id].parentId
        ? state.pagewebLayout.entities.sections[
            state.pagewebLayout.relations.groups[id].parentId
          ]
        : undefined,
    })
  );
  const dispatch = useThunkDispatch(() =>
    showToast({
      message: t('error_api_toast'),
      isMobile: requestContext.requestInfo.display.isMobile,
    })
  );
  const onFetchGroup = useCallback(() => {
    if (!!id && fetchStatus === FetchStatus.Idle) {
      if (parentSection?.type === SectionType.HelixTheme) {
        dispatch(fetchDynamicLayoutByGroupId({groupId: id}));
      } else {
        dispatch(fetchViewDataByGroupId({groupId: id}));
      }
    }
  }, [id, fetchStatus, parentSection?.type, dispatch]);

  const isFetchSuccess = fetchStatus === FetchStatus.Success;
  const isLoading =
    fetchStatus === FetchStatus.Loading ||
    fetchStatus === FetchStatus.Idle ||
    fetchStatus === FetchStatus.Error ||
    !group;

  const ChoosedComponent =
    !group.type || !layoutGroupTypeComponentMap[group.type]
      ? Hidden
      : layoutGroupTypeComponentMap[group.type];

  return (
    <div
      {...putTiaraObjectProps({
        customProps: {content_cnt: String(itemsInGroup?.length)},
      })}
      className={twMerge(
        group.type !== GroupType.HorizontalScroll &&
          group.type !== GroupType.BigBanner &&
          group.type !== GroupType.ResponsiveBanner &&
          'px-11pxr' /* 모든 섹션의 Group(타이틀, 탭 제외)에 공통으로 좌우 패딩 11px 가 들어갑니다 */
      )}
    >
      <SkeletonLazyLoad
        onFetch={onFetchGroup}
        isFetchSuccess={isFetchSuccess}
        isLoading={isLoading}
        group={group}
      >
        <ChoosedComponent group={group}>
          {/* nested 된 group 이 존재할 수 있다(group 을 묶음 그룹이 존재할 수 있음) */}
          {groupsInGroup?.map(nestedGroupId => (
            <Group key={nestedGroupId} id={nestedGroupId} />
          ))}
          {itemsInGroup?.map((itemId, index) => (
            <div
              key={`${group.type}-${itemId}`}
              {...putTiaraObjectProps({
                viewimp_contents: {
                  imp_id: getImpId({
                    sectionType: parentSection?.type,
                    groupType: group.type,
                    index,
                  }),
                  imp_ordnum: String(index + 1),
                },
                click: {
                  ordnum: String(index + 1),
                  imp_id: getImpId({
                    sectionType: parentSection?.type,
                    groupType: group.type,
                    index,
                  }),
                },
              })}
            >
              <Item id={itemId} groupId={id} />
            </div>
          ))}
        </ChoosedComponent>
      </SkeletonLazyLoad>
    </div>
  );
}

const layoutGroupTypeComponentMap: {
  [key in GroupType]: FC<CommonGroupProps>;
} = {
  [GroupType.BigBanner]: Banner,
  [GroupType.ResponsiveBanner]: ResponsiveBanner,
  [GroupType.EventBanner]: EventBannerGrid,
  [GroupType.LineBanner]: PassChildren,
  [GroupType.PromotionBanner]: PassChildren,
  [GroupType.DisplayAd]: DisplayAd,
  [GroupType.StrategyView]: StrategyCategory,
  [GroupType.CardView]: ThumbnailGrid,
  [GroupType.MainRankingListView]: MainRankingNormalList,
  [GroupType.ListView]: NormalList,
  [GroupType.CleanView]: ThumbnailGrid,
  [GroupType.HorizontalScroll]: HorizontalScroll,
  [GroupType.ContentRecommend]: PassChildren,
  [GroupType.PosterView]: ThumbnailGrid,
  [GroupType.StandaloneBannerAndSubCard]: StandaloneBannerAndSubCard,
  [GroupType.SubCard]: SubCard,
  [GroupType.TodaySeries]: TodaySeries,
  [GroupType.StandaloneBanner]: PassChildren,
  [GroupType.Unknown]: Hidden,
};

function getImpId({
  sectionType,
  groupType,
  index,
}: {
  sectionType?: SectionType;
  groupType: GroupType;
  index: number;
}) {
  if (sectionType === SectionType.Top && groupType === GroupType.BigBanner) {
    return `stop_b_00${index + 1}`;
  }
  if (
    sectionType === SectionType.BigBanner &&
    (groupType === GroupType.ResponsiveBanner ||
      groupType === GroupType.BigBanner)
  ) {
    return `sbanner_b_00${index + 1}`;
  }
  return;
}

export default Group;
